/**
 * Buffers slideshow song audio ahead of playback — the audio counterpart to
 * slideshowImagePreloader. Given a resolved stream URL, it creates a detached
 * HTMLAudioElement with `preload='auto'` so the browser fetches the media before the
 * song's window is reached on the timeline.
 *
 * Crucially, the element that buffers a song is also the element useSlideshowAudio
 * plays: at the song's boundary the hook just unmutes and calls play() on the
 * already-loaded element, so there is no second network request and no cold-fetch
 * stall. (An earlier approach used a throwaway buffering element separate from a
 * single playback element, but browsers don't reliably share buffered media between
 * two elements — and these stream responses carry no Cache-Control — so the playback
 * element re-fetched. Reusing the buffered element avoids that entirely.)
 *
 * Elements are created muted so a buffering song can never sound before the hook
 * activates it. Buffering reuses the exact URL playback will use (via the shared
 * stream-URL cache in useSlideshowAudio): a separately re-signed URL would waste the
 * download.
 */

export type PreloadedAudioElements = Map<string, HTMLAudioElement>;

/**
 * Starts buffering a song's audio if it isn't already, and returns the element for it.
 * Keyed by song id so each song buffers exactly once across re-renders; returns the
 * existing element when already buffering, or null when no url is available.
 */
export function bufferSongAudio(
    elements: PreloadedAudioElements,
    songId: string,
    url: string
): HTMLAudioElement | null {
    const existing = elements.get(songId);

    if (existing) {
        return existing;
    }

    if (!url) {
        return null;
    }

    const audio = new Audio();

    // `preload='auto'` asks the browser to fetch the media data now, not just metadata.
    audio.preload = 'auto';
    // Muted while buffering so it can't sound before the hook activates it for playback.
    audio.muted = true;
    audio.src = url;
    // Some browsers won't begin fetching until load() is called after src is assigned.
    audio.load();

    elements.set(songId, audio);

    return audio;
}

/**
 * Stops and releases a single buffered element (aborting any in-flight fetch) and drops
 * it from the map. A no-op when that song isn't buffered.
 */
function releaseSong(elements: PreloadedAudioElements, songId: string): void {
    const audio = elements.get(songId);

    if (!audio) {
        return;
    }

    audio.pause();
    audio.src = '';
    // Abort any pending network request and release buffered data.
    audio.load();
    elements.delete(songId);
}

/**
 * Releases every buffered element whose song id is NOT in `keepSongIds`, leaving the
 * rest (and their in-progress buffering / current playback) untouched. Used when the
 * timeline's song set changes so songs that are still present aren't needlessly torn
 * down and re-downloaded.
 */
export function releaseBufferedAudioExcept(
    elements: PreloadedAudioElements,
    keepSongIds: ReadonlySet<string>
): void {
    Array.from(elements.keys()).forEach((songId) => {
        if (!keepSongIds.has(songId)) {
            releaseSong(elements, songId);
        }
    });
}

/**
 * Tears down every buffered element — stops in-flight fetches and releases the media
 * so detached audio elements don't leak. Clears the map so the same instance can be
 * reused for a new set of songs.
 */
export function releaseBufferedAudio(elements: PreloadedAudioElements): void {
    elements.forEach((audio) => {
        releaseElement(audio);
    });

    elements.clear();
}

/** Stops one element and releases its media, aborting any in-flight fetch. */
function releaseElement(audio: HTMLAudioElement): void {
    audio.pause();
    audio.src = '';
    // Abort any pending network request and release buffered data.
    audio.load();
}

/**
 * Like `releaseBufferedAudio`, but ramps whatever is currently sounding down to silence over
 * `durationMs` first, then releases it — so a song ends on a fade rather than a hard cut.
 *
 * The ramp deliberately outlives the React unmount that triggers it: the elements are detached
 * (never in the document), so they can keep sounding while the component that owned them goes
 * away. The map is cleared up front so nothing can adopt an element mid-fade; the fade runs on a
 * local list and releases each element when it reaches silence.
 */
export function fadeOutAndReleaseBufferedAudio(
    elements: PreloadedAudioElements,
    durationMs: number
): void {
    const sounding: HTMLAudioElement[] = [];

    elements.forEach((audio) => {
        if (!audio.paused && !audio.muted && audio.volume > 0) {
            sounding.push(audio);
        } else {
            // Nothing audible to fade — release these immediately.
            releaseElement(audio);
        }
    });

    elements.clear();

    if (durationMs <= 0 || sounding.length === 0) {
        sounding.forEach(releaseElement);

        return;
    }

    const startVolumes = sounding.map((audio) => audio.volume);
    const startedAt = performance.now();

    const step = () => {
        const progress = Math.min(1, (performance.now() - startedAt) / durationMs);

        sounding.forEach((audio, index) => {
            audio.volume = Math.max(0, startVolumes[index] * (1 - progress));
        });

        if (progress < 1) {
            window.requestAnimationFrame(step);

            return;
        }

        sounding.forEach(releaseElement);
    };

    window.requestAnimationFrame(step);
}
